/**
 * Garden → throw → rabbit-eats toy for Hubert (corn) and Holly Springs (tomato).
 *
 * Pure logic: yard config, throw targets, and eligibility. Per-yard runtime
 * state lives in `gardenFoodRuntime.ts`.
 */

import type {
  InteractiveObject,
  SpawnPoint,
  WalkObstacle,
  WorldSceneryLayer,
} from '@/world-prototype/types';
import { GARDEN_HIT_MIN_CSS_PX } from '@/world-prototype/interaction/playerInteraction';
import {
  pointInAnyObstacle,
  pointInPolygon,
} from '@/world-prototype/movement/walkArea';
import { rabbitDistance } from '@/world-prototype/creatures/rabbitBehavior';
import type { RabbitYardConfig } from '@/world-prototype/creatures/rabbitBehavior';

export const GARDEN_OBJECT_ID = 'garden-bed';

export type GardenFoodKind = 'corn' | 'tomato';

/** How long after the rabbit eats before the garden can be used again. */
export const GARDEN_COOLDOWN_MS = 7_000;

/** Stand-off when harvesting from the garden bed. */
export const GARDEN_APPROACH_DISTANCE = 0.034;

/** Brief beat while the character pulls produce (ms). */
export const GARDEN_PULL_MS = 280;

/** Character toss anticipation before release (ms). */
export const GARDEN_THROW_CHAR_MS = 380;

/**
 * On-screen crop height in feet — kid-readable exaggeration (real corn is
 * much smaller; toys read better oversized).
 */
export const GARDEN_FOOD_HEIGHT_FEET = 1.35;

const SCATTER = `${import.meta.env.BASE_URL}art/environments/shared/scatter`;

export const GARDEN_FOOD_SRC: Readonly<Record<GardenFoodKind, string>> = {
  corn: `${SCATTER}/corn-v1.png`,
  tomato: `${SCATTER}/tomato-v1.png`,
};

export const GARDEN_FOOD_IMAGE = {
  corn: { imageWidth: 64, imageHeight: 96 },
  tomato: { imageWidth: 72, imageHeight: 72 },
} as const;

interface GardenYardConfig {
  yardId: string;
  kind: GardenFoodKind;
  /** Offsets from the garden prop center toward open grass. */
  throwOffsets: readonly SpawnPoint[];
}

const HUBERT_GARDEN: GardenYardConfig = {
  yardId: 'hubert',
  kind: 'corn',
  throwOffsets: [
    { x: 0.1, y: -0.055 },
    { x: 0.13, y: -0.075 },
    { x: 0.08, y: -0.04 },
    { x: 0.15, y: -0.05 },
  ],
};

const HOLLY_SPRINGS_GARDEN: GardenYardConfig = {
  yardId: 'raleigh',
  kind: 'tomato',
  throwOffsets: [
    { x: 0.11, y: -0.07 },
    { x: 0.14, y: -0.05 },
    { x: 0.09, y: -0.085 },
    { x: 0.16, y: -0.065 },
  ],
};

const GARDEN_YARDS: Readonly<Record<string, GardenYardConfig>> = {
  hubert: HUBERT_GARDEN,
  raleigh: HOLLY_SPRINGS_GARDEN,
};

export function gardenFoodConfigForYard(
  yardId: string,
): GardenYardConfig | null {
  return GARDEN_YARDS[yardId] ?? null;
}

export function gardenFoodKindForYard(yardId: string): GardenFoodKind | null {
  return GARDEN_YARDS[yardId]?.kind ?? null;
}

export function isGardenFoodYard(yardId: string): boolean {
  return yardId in GARDEN_YARDS;
}

export function isGardenObject(objectId: string): boolean {
  return objectId === GARDEN_OBJECT_ID;
}

/** World-width hit radius that guarantees a kid-friendly garden tap target. */
export function gardenTouchHitRadiusWorld(
  worldPx: number,
  authoredRadius = 0,
): number {
  const minRadius = GARDEN_HIT_MIN_CSS_PX / Math.max(1, worldPx) / 2;
  return Math.max(authoredRadius, minRadius);
}

/**
 * Hotspot center + size for the visible garden bed (not the tiny authored hit offset).
 */
export function gardenHotspotLayout(
  obj: InteractiveObject,
  layer: Pick<WorldSceneryLayer, 'x' | 'y' | 'width' | 'height'> | null,
  worldAspect: number,
  worldPx: number,
): { x: number; y: number; hitW: number; hitH: number } {
  const aspect = worldAspect > 0 ? worldAspect : 1;
  const minFrac = GARDEN_HIT_MIN_CSS_PX / Math.max(1, worldPx);
  let hitW = Math.max(obj.hitRadius * 2, minFrac * 1.2);
  let hitH = Math.max(obj.hitRadius * 2 * aspect, minFrac * 1.05);
  let x = obj.x;
  let y = obj.y;

  if (layer) {
    const layerH = layer.height ?? obj.hitRadius * 2;
    const layerW = layer.width ?? obj.hitRadius * 2;
    const layerX = layer.x ?? obj.x;
    const layerY = layer.y ?? obj.y;
    x = layerX;
    y = layerY - layerH * 0.4;
    hitW = Math.max(layerW * 1.15, hitW);
    hitH = Math.max(layerH * 0.95, hitH);
  }

  return { x, y, hitW, hitH };
}

/** True when a ground tap lands on the visible garden bed (generous rect). */
export function pointInGardenHotspot(
  point: SpawnPoint,
  obj: InteractiveObject,
  layer: Pick<WorldSceneryLayer, 'x' | 'y' | 'width' | 'height'> | null,
  worldAspect: number,
  worldPx: number,
): boolean {
  const { x, y, hitW, hitH } = gardenHotspotLayout(
    obj,
    layer,
    worldAspect,
    worldPx,
  );
  const aspect = worldAspect > 0 ? worldAspect : 1;
  const halfW = hitW / 2;
  const halfH = hitH / aspect / 2;
  return (
    Math.abs(point.x - x) <= halfW && Math.abs(point.y - y) <= halfH
  );
}

/** Minimum distance from the rabbit so tossed food is not dropped on its head. */
const MIN_RABBIT_CLEARANCE = 0.07;

/** Minimum distance from the garden center so the toss reads as a throw. */
const MIN_GARDEN_CLEARANCE = 0.045;

/**
 * Pick a grassy landing spot near the garden. Tries authored offsets first,
 * then jitters within the rabbit roam polygon.
 */
export function pickGardenThrowTarget(
  garden: SpawnPoint,
  rabbitConfig: RabbitYardConfig,
  rabbitPos: SpawnPoint | null,
  obstacles: readonly WalkObstacle[] | undefined,
  worldAspect: number,
  rng: () => number = Math.random,
): SpawnPoint {
  const offsets = [...GARDEN_YARDS[rabbitConfig.yardId]?.throwOffsets ?? []];
  // Shuffle offsets so repeats feel less identical.
  for (let i = offsets.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [offsets[i], offsets[j]] = [offsets[j], offsets[i]];
  }

  const isValid = (point: SpawnPoint): boolean => {
    if (!pointInPolygon(point.x, point.y, rabbitConfig.roamPolygon)) {
      return false;
    }
    if (
      obstacles &&
      obstacles.length > 0 &&
      pointInAnyObstacle(point.x, point.y, obstacles)
    ) {
      return false;
    }
    if (
      rabbitPos &&
      rabbitDistance(point, rabbitPos, worldAspect) < MIN_RABBIT_CLEARANCE
    ) {
      return false;
    }
    if (rabbitDistance(point, garden, worldAspect) < MIN_GARDEN_CLEARANCE) {
      return false;
    }
    return true;
  };

  for (const offset of offsets) {
    const candidate = { x: garden.x + offset.x, y: garden.y + offset.y };
    if (isValid(candidate)) return candidate;
  }

  const poly = rabbitConfig.roamPolygon;
  const xs = poly.map((p) => p.x);
  const ys = poly.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  for (let attempt = 0; attempt < 32; attempt += 1) {
    const candidate = {
      x: minX + rng() * (maxX - minX),
      y: minY + rng() * (maxY - minY),
    };
    if (isValid(candidate)) return candidate;
  }

  // Last resort: nudge toward lawn center from the garden.
  return {
    x: garden.x + 0.1,
    y: garden.y - 0.06,
  };
}
